import { useEffect, useState } from 'react'
import Listing from './Listing/Listing'
import Header from './Header'
import SideBarButton from './SideMenuButton'
import { GridItemData } from 'Vendor/Utils/StudioTypes'
import { useSelector } from 'react-redux'
import EditImage from './EditImage'
import EditText from './EditText'
import { selectGroupSelectedItems, selectSingleSelectedItem } from 'Vendor/Store/studio/selectors'
import TextBlock from './TextBlock'
import { RootState } from 'Vendor/Store/store'
import ColorPalette from './ColorPalette'
import { ActiveToolState, isToolActive, selectActiveTool } from 'Vendor/Store/studio/activeToolSlice'

type EditMode = 'image' | 'text' | 'color'

interface SideMenuProps {
    creationId: number
    title: string
    setShow: (show: boolean, reload?: boolean) => void;
    forceSave: () => Promise<void>;
    recentUsedColors: {name: string, value: string}[]
}

const SideMenu = ({ creationId, title, setShow, forceSave, recentUsedColors }: SideMenuProps) => {
    const [editMode, setEditMode] = useState<EditMode>('image')
    const selectedItem = useSelector(selectSingleSelectedItem)
    const [localItem, setLocalItem] = useState<GridItemData | null>(null);
    const platformFonts = useSelector((state: RootState) => state.assets.fonts);
    const platformBrandColors = useSelector((state: RootState) => state.assets.colors);
    const [overrideType, setOverrideType] = useState<string | undefined>(undefined);
    const selectedGroupItems = useSelector(selectGroupSelectedItems)

    const activeTool = useSelector(selectActiveTool)

    useEffect(() => {
        const hasGroup = selectedGroupItems && selectedGroupItems.length > 0;

        if (!selectedItem && !hasGroup) {
            setLocalItem(null);
            setOverrideType(undefined);
            return;
        }

        if (hasGroup) {
            setLocalItem(null);

            if (isToolActive(activeTool, activeTool.iconType, undefined, 'group')) {
                handleIconAction(activeTool);
            } else {
                setOverrideType(undefined);
            }
        } else if (selectedItem) {
            setLocalItem({ ...selectedItem });

            if (isToolActive(activeTool, activeTool.iconType, selectedItem.id, selectedItem.type)) {
                handleIconAction(activeTool);
            } else {
                setOverrideType(undefined);
            }
        }
    }, [selectedItem, activeTool, selectedGroupItems]);

    const handleIconAction = (item: ActiveToolState) => {
        const { iconType } = item;
    
        switch (iconType) {
            case "backgroundColor":
            case "outlineColor":
                setOverrideType("COLOR");
                break;
            case "image":
                setOverrideType("PRODUCT");
                break;
            case "text":
                setOverrideType("TEXT");
                break;
            default:
                setOverrideType(undefined);
                break;
        }
    }

    const handleModeChange = (type: EditMode) => {
        if (
            localItem &&
            isToolActive(activeTool, activeTool.iconType, localItem.id,  localItem.type)
        ) {
            return;
        }

        setEditMode(type);
        setOverrideType(undefined);
    };

    const renderContent = () => {
        const typeComponentMap: Record<string, JSX.Element> = {
            TEXT: <EditText platformFonts={platformFonts} />,
            PRODUCT: <EditImage />,
            FILE: <EditImage />,
            EXTERNAL_IMAGE: <EditImage />,
            COLOR: <ColorPalette recentColors={recentUsedColors} defaultColors={platformBrandColors} field={activeTool.iconType} />,
        };

        const editModeMap: Record<string, JSX.Element> = {
            color: <ColorPalette recentColors={recentUsedColors} defaultColors={platformBrandColors} draggable={true} />,
            text: <TextBlock platformFonts={platformFonts} />,
            image: <Listing creationId={creationId} />,
        };

        const typeToRender = overrideType || localItem?.type;
        if (typeToRender && typeComponentMap[typeToRender]) {
            return typeComponentMap[typeToRender];
        }

        if (editMode && editModeMap[editMode]) {
            return editModeMap[editMode];
        }

        return <Listing creationId={creationId} />;
    };

    return (
        <div className="layout-container">
            <div id="studio-menu">
                <Header title={title} setShow={setShow} forceSave={forceSave} />
                {renderContent()}
            </div>
            <div className="menu-buttons d-flex flex-column justify-content-center align-items-center space-y-4">
                <SideBarButton
                    isActive={editMode === 'image'}
                    onClick={() => handleModeChange('image')}
                    iconClass="fa-solid fa-images fs-2"
                />
                <SideBarButton
                    isActive={editMode === 'text'}
                    onClick={() => handleModeChange('text')}
                    iconClass="fa-solid fa-a fs-2"
                />
                <SideBarButton
                    isActive={editMode === 'color'}
                    onClick={() => handleModeChange('color')}
                    iconClass="fa-regular fa-palette fs-2"
                />
            </div>
        </div>
    )
}

export default SideMenu;
